// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { Maximize2, Minimize2, Minus, Sparkles, SquarePen } from "lucide-react";
import { type RefObject, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { AssistantMessageView } from "@/features/assistant/assistant-message";
import { useAssistant } from "@/features/assistant/assistant-provider";
import { isBusy } from "@/features/assistant/assistant-reducer";
import { ContextDivider } from "@/features/assistant/blocks/context-divider";
import { Composer } from "@/features/assistant/composer";
import { EmptyState } from "@/features/assistant/empty-state";
import { useIsMobile } from "@/hooks/use-mobile";

const STICK_THRESHOLD_PX = 40;

export function AssistantPanel({ composerRef }: { composerRef: RefObject<HTMLTextAreaElement | null> }) {
  const { t } = useTranslation();
  const { state, context, ask, stop, retry, reset, close, setExpanded, dismissContext, setFeedback } = useAssistant();
  const isMobile = useIsMobile();
  const busy = isBusy(state);
  const listRef = useRef<HTMLDivElement>(null);
  const stickToBottom = useRef(true);
  const lastAnswerId = [...state.messages].reverse().find((m) => m.role === "assistant")?.id;

  // biome-ignore lint/correctness/useExhaustiveDependencies: new or growing messages are the trigger; the body reads the DOM, not the state
  useEffect(
    function followNewContent() {
      const list = listRef.current;
      if (list && stickToBottom.current) list.scrollTop = list.scrollHeight;
    },
    [state.messages],
  );

  function onScroll() {
    const list = listRef.current;
    if (!list) return;
    stickToBottom.current = list.scrollHeight - list.scrollTop - list.clientHeight < STICK_THRESHOLD_PX;
  }

  function send(question: string): boolean {
    stickToBottom.current = true;
    return ask(question);
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex items-center gap-1 border-b px-3 py-2">
        <div className="flex flex-1 items-center gap-2 font-semibold text-sm">
          <span className="flex size-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Sparkles className="size-3.5" />
          </span>
          {t("assistant.title")}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="size-8"
          aria-label={t("assistant.newConversation")}
          disabled={state.messages.length === 0}
          onClick={reset}
        >
          <SquarePen className="size-4" />
        </Button>
        {!isMobile && (
          <Button
            variant="ghost"
            size="icon"
            className="size-8"
            aria-label={t(state.isExpanded ? "assistant.collapse" : "assistant.expand")}
            onClick={() => setExpanded(!state.isExpanded)}
          >
            {state.isExpanded ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
          </Button>
        )}
        <Button variant="ghost" size="icon" className="size-8" aria-label={t("assistant.minimise")} onClick={close}>
          <Minus className="size-4" />
        </Button>
      </header>

      <div
        ref={listRef}
        onScroll={onScroll}
        className="flex-1 overflow-y-auto overscroll-contain px-4 py-3"
        aria-live="polite"
      >
        {state.messages.length === 0 ? (
          <EmptyState context={context} onPick={send} />
        ) : (
          state.messages.map((m) => {
            if (m.role === "user") {
              return (
                <div
                  key={m.id}
                  data-role="user-message"
                  className="mb-3 ml-auto w-fit max-w-[85%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-primary/10 px-3 py-2 text-sm"
                >
                  {m.text}
                </div>
              );
            }
            if (m.role === "divider") return <ContextDivider key={m.id} label={m.label} />;
            return (
              <AssistantMessageView
                key={m.id}
                message={m}
                isLast={m.id === lastAnswerId}
                busy={busy}
                onRetry={() => retry(m.id)}
                onFeedback={(v) => setFeedback(m.id, v)}
                onNavigate={isMobile ? close : undefined}
              />
            );
          })
        )}
      </div>

      <Composer
        inputRef={composerRef}
        busy={busy}
        hasMessages={state.messages.length > 0}
        context={context}
        onSend={send}
        onStop={stop}
        onDismissContext={dismissContext}
      />
    </div>
  );
}
